import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { storyAPI } from '../shared/services/api';
import { friendshipAPI } from '../services/friendshipApi';
import LoadingSpinner from '../components/LoadingSpinner';
import StoryCard from '../features/stories/components/StoryCard';
import { useFriends } from '../features/social/hooks/useFriends';
import { Select } from '../shared/components/UI';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const DashboardHeader = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const DashboardTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const DashboardSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--color-gray-600);
  margin-bottom: 2rem;
`;

const CreateStoryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: var(--color-primary);
  color: var(--color-gray-800);
  text-decoration: none;
  border-radius: var(--border-radius-lg);
  font-weight: 600;
  font-size: 1.1rem;
  box-shadow: var(--shadow-md);
  transition: all 0.2s ease;

  &:hover {
    background: #8BB5E0;
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

// Old filter components removed - using new enhanced filters below

const StoriesSection = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 1.5rem;
`;

const StoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-gray-500);
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-600);
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  color: var(--color-gray-500);
  margin-bottom: 2rem;
`;

// Enhanced Filter Components
const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 150px;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-gray-700);
`;

const FilterInput = styled.input`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &::placeholder {
    color: var(--color-gray-400);
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const ClearFiltersButton = styled.button`
  padding: 0.75rem 1rem;
  background: var(--color-gray-100);
  color: var(--color-gray-600);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-gray-200);
    color: var(--color-gray-700);
  }
`;

// Tab Components
const TabsContainer = styled.div`
  display: flex;
  background: var(--color-gray-50);
  border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
  border-bottom: 1px solid var(--color-gray-200);
  margin-bottom: 0;
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 1rem;
  border: none;
  background: ${props => props.active ? 'var(--color-white)' : 'transparent'};
  color: ${props => props.active ? 'var(--color-gray-900)' : 'var(--color-gray-600)'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 1rem;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? 'var(--color-primary)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-white);
    color: var(--color-gray-900);
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  max-width: 450px;
  width: 100%;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalText = styled.p`
  color: var(--color-gray-700);
  margin: 0;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    if (props.$variant === 'danger') {
      return `
        background: var(--color-red-600);
        border-color: var(--color-red-600);
        color: white;
        &:hover {
          background: var(--color-red-700);
        }
      `;
    }
    return `
      background: white;
      border-color: var(--color-gray-300);
      color: var(--color-gray-700);
      &:hover {
        background: var(--color-gray-50);
      }
    `;
  }}
`;

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [myStories, setMyStories] = useState<any[]>([]);
  const [friendStories, setFriendStories] = useState<any[]>([]);
  const [allStories, setAllStories] = useState<any[]>([]);
  const [filteredStories, setFilteredStories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedFriend, setSelectedFriend] = useState('');
  const [genres, setGenres] = useState<string[]>([]);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState<{ id: string; title: string } | null>(null);

  // Friends data
  const { friends, loadFriends } = useFriends();
  

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        await loadFriends();
        await loadAllStories();
        
        // Check URL parameters for navigation from Friends page
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        const friendParam = urlParams.get('friend');
        
        if (tabParam === 'friends') {
          setActiveTab('friends');
        }
        if (friendParam) {
          setSelectedFriend(friendParam);
        }
      } catch (error) {
        console.error('Failed to initialize dashboard:', error);
      }
    };
    
    initializeDashboard();
  }, []);

  // Reload stories when friends data changes
  useEffect(() => {
    if (friends.length > 0) {
      loadAllStories();
    }
  }, [friends]);

  useEffect(() => {
    filterStories();
  }, [myStories, friendStories, allStories, activeTab, searchText, selectedGenre, selectedFriend]);

  const loadAllStories = async () => {
    try {
      setLoading(true);
      
      // Load my stories
      const myStoriesResponse = await storyAPI.getMyStories();
      const myStoriesData = myStoriesResponse.data || [];
      setMyStories(myStoriesData);
      
      // Load friends' stories (only friends' stories, not including mine)
      let friendStoriesData: any[] = [];
      if (friends.length > 0) {
        const friendStoriesPromises = friends.map(async (friend) => {
          try {
            const response = await storyAPI.getAccessibleStoriesByAuthor(friend.userId);
            const stories = response.data || [];
            return stories.map((story: any) => ({
              ...story,
              authorName: friend.fullName || friend.username,
              isFromFriend: true
            }));
          } catch (error) {
            console.error(`Failed to load stories for friend ${friend.username}:`, error);
            return [];
          }
        });
        
        const friendStoriesArrays = await Promise.all(friendStoriesPromises);
        friendStoriesData = friendStoriesArrays.flat();
      }
      setFriendStories(friendStoriesData);
      
      // Load all public stories
      const publicStoriesResponse = await storyAPI.searchPublicStories({ size: 1000 });
      const publicStoriesData = publicStoriesResponse.data.stories || [];
      
      // All Stories = Public stories + My stories + Friends' stories (combined, deduplicated)
      const combinedStories = [
        ...publicStoriesData,
        ...myStoriesData,
        ...friendStoriesData
      ];
      
      // Remove duplicates based on story ID
      const uniqueAllStories = combinedStories.filter((story, index, array) => 
        array.findIndex(s => s.id === story.id) === index
      );
      
      setAllStories(uniqueAllStories);
      
      // Extract unique genres from all stories
      const uniqueGenres = Array.from(new Set(uniqueAllStories.map((story: any) => story.genre).filter(Boolean)));
      setGenres(uniqueGenres as string[]);
      
    } catch (error) {
      console.error('Failed to load stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const filterStories = () => {
    let currentStories: any[] = [];
    
    // Get stories based on active tab
    switch (activeTab) {
      case 'my':
        currentStories = myStories || [];
        break;
      case 'friends':
        currentStories = friendStories || [];
        break;
      case 'all':
        currentStories = allStories || [];
        break;
      default:
        currentStories = myStories || [];
    }
    
    // Apply filters
    let filtered = currentStories;
    
    // Filter by search text
    if (searchText.trim()) {
      filtered = filtered.filter(story => 
        story.title.toLowerCase().includes(searchText.toLowerCase()) ||
        story.summary?.toLowerCase().includes(searchText.toLowerCase()) ||
        (story.author?.username && story.author.username.toLowerCase().includes(searchText.toLowerCase())) ||
        (story.authorName && story.authorName.toLowerCase().includes(searchText.toLowerCase()))
      );
    }
    
    // Filter by genre
    if (selectedGenre) {
      filtered = filtered.filter(story => story.genre === selectedGenre);
    }
    
    // Filter by friend/author (for "friends" and "all" tabs)
    if (selectedFriend && (activeTab === 'all' || activeTab === 'friends')) {
      filtered = filtered.filter(story => {
        // Get the actual author name that would be displayed
        const storyAuthorDisplayName = story.authorName || 
                                      story.author?.fullName || 
                                      story.author?.username ||
                                      `${story.author?.firstName || ''} ${story.author?.lastName || ''}`.trim() ||
                                      'Unknown Author';
        
        return storyAuthorDisplayName === selectedFriend;
      });
    }
    
    setFilteredStories(filtered);
  };
  
  const clearFilters = () => {
    setSearchText('');
    setSelectedGenre('');
    setSelectedFriend('');
  };

  const openDeleteModal = (storyId: string, storyTitle: string) => {
    setStoryToDelete({ id: storyId, title: storyTitle });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setStoryToDelete(null);
  };

  const handleDeleteStory = async () => {
    if (!storyToDelete) return;

    try {
      await storyAPI.deleteStory(storyToDelete.id);
      setMyStories(myStories.filter(story => story.id !== storyToDelete.id));
      toast.success('Story deleted successfully');
      closeDeleteModal();
    } catch (error) {
      console.error('Failed to delete story:', error);
      toast.error('Failed to delete story');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <DashboardTitle>{t('dashboard.title')}</DashboardTitle>
        <DashboardSubtitle>
          {t('dashboard.subtitle')}
        </DashboardSubtitle>
        <CreateStoryButton to="/story/new">
          ✏️ {t('dashboard.createNewStory')}
        </CreateStoryButton>
      </DashboardHeader>

      {/* Tabs */}
      <TabsContainer>
        <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          {t('dashboard.allStories')} ({allStories.length})
        </Tab>
        <Tab active={activeTab === 'friends'} onClick={() => setActiveTab('friends')}>
          {t('dashboard.friendsStories')} ({friendStories.length})
        </Tab>
        <Tab active={activeTab === 'my'} onClick={() => setActiveTab('my')}>
          {t('dashboard.myStories')} ({myStories.length})
        </Tab>
      </TabsContainer>

      {/* Filters */}
      <FilterGrid>
          {/* Show search and genre filters for all tabs */}
          <FilterGroup>
            <FilterLabel>{t('dashboard.searchStories')}</FilterLabel>
            <FilterInput
              type="text"
              placeholder={t('dashboard.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </FilterGroup>
          
          <Select
            label={t('dashboard.genre')}
            value={selectedGenre}
            onChange={setSelectedGenre}
            options={[
              { value: '', label: t('dashboard.allGenres') },
              ...genres.map(genre => ({
                value: genre,
                label: t(`genres.${genre}`, genre)
              }))
            ]}
          />
          
          {/* Show friend/author filter for friends and all tabs */}
          {(activeTab === 'all' || activeTab === 'friends') && (
            <Select
              label={activeTab === 'all' ? t('dashboard.author') : t('dashboard.friend')}
              value={selectedFriend}
              onChange={setSelectedFriend}
              options={
                activeTab === 'all' ? [
                  { value: '', label: t('dashboard.allAuthors') },
                  // For All Stories tab - show all unique authors from all stories (use display names)
                  ...Array.from(new Set(allStories.map(story =>
                    story.authorName ||
                    story.author?.fullName ||
                    story.author?.username ||
                    `${story.author?.firstName || ''} ${story.author?.lastName || ''}`.trim() ||
                    'Unknown Author'
                  ).filter(Boolean))).map((author) => ({
                    value: author,
                    label: author
                  }))
                ] : [
                  { value: '', label: t('dashboard.allFriends') },
                  // For Friends Stories tab - show friends
                  ...(friends && friends.length > 0
                    ? friends.map((friend) => ({
                        value: friend.fullName || friend.username,
                        label: friend.fullName || friend.username
                      }))
                    : [{ value: '', label: t('dashboard.noFriendsYet'), disabled: true } as any]
                  )
                ]
              }
            />
          )}
          
          <div>
            <ClearFiltersButton onClick={clearFilters}>
              {t('dashboard.clearFilters')}
            </ClearFiltersButton>
          </div>
        </FilterGrid>

      <StoriesSection>
        <SectionTitle>
          {activeTab === 'my' && t('dashboard.myStories')}
          {activeTab === 'friends' && t('dashboard.friendsStories')}
          {activeTab === 'all' && t('dashboard.allPublicStories')}
          {' '}({filteredStories.length})
        </SectionTitle>

        {filteredStories.length > 0 ? (
          <StoriesGrid>
            {filteredStories.map((story) => (
              <StoryCard 
                key={story.id} 
                story={story} 
                showAuthor={activeTab === 'friends' || activeTab === 'all'}
                showActions={activeTab === 'my'}
                onEdit={activeTab === 'my' ? () => window.location.href = `/story/edit/${story.id}` : undefined}
                onDelete={activeTab === 'my' ? () => openDeleteModal(story.id, story.title) : undefined}
              />
            ))}
          </StoriesGrid>
        ) : (
          <EmptyState>
            <EmptyStateIcon>📚</EmptyStateIcon>
            <EmptyStateTitle>
              {activeTab === 'my' && t('dashboard.noStoriesYet')}
              {activeTab === 'friends' && t('dashboard.noFriendsStories')}
              {activeTab === 'all' && t('dashboard.noStoriesFound')}
            </EmptyStateTitle>
            <EmptyStateText>
              {activeTab === 'my' && t('dashboard.startCreating')}
              {activeTab === 'friends' && t('dashboard.friendsNoStories')}
              {activeTab === 'all' && t('dashboard.tryAdjustingFilters')}
            </EmptyStateText>
            {activeTab === 'my' && (
              <CreateStoryButton to="/story/new">
                ✏️ {t('dashboard.createFirstStory')}
              </CreateStoryButton>
            )}
          </EmptyState>
        )}
      </StoriesSection>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && storyToDelete && (
        <ModalOverlay onClick={closeDeleteModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Delete Story</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Are you sure you want to delete "<strong>{storyToDelete.title}</strong>"?
                This action cannot be undone.
              </ModalText>
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={closeDeleteModal}>
                Cancel
              </ModalButton>
              <ModalButton $variant="danger" onClick={handleDeleteStory}>
                Delete Story
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;